import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiPost } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Section } from '../../../components/ui/Section';

export default function SmtpConfigSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    smtpHost: '', smtpPort: 587, smtpUser: '', smtpPass: '', smtpFrom: '', smtpSecure: false, smtpEnabled: false,
  });
  const [loaded, setLoaded] = useState(false);
  const [passSet, setPassSet] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const { data } = useQuery({ queryKey: ['system-smtp'], queryFn: () => apiGet<any>('/system/smtp') });

  useEffect(() => {
    if (data && !loaded) {
      setForm({ smtpHost: data.smtpHost ?? '', smtpPort: data.smtpPort ?? 587, smtpUser: data.smtpUser ?? '', smtpPass: '', smtpFrom: data.smtpFrom ?? '', smtpSecure: data.smtpSecure ?? false, smtpEnabled: data.smtpEnabled ?? false });
      setPassSet(data.smtpPassSet);
      setLoaded(true);
    }
  }, [data, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => apiPatch('/system/smtp', { ...form, smtpPass: form.smtpPass || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['system-smtp'] }); toast.success('Config SMTP salvata'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMutation = useMutation({
    mutationFn: () => apiPost('/system/smtp/test', { to: testEmail }),
    onSuccess: () => toast.success(`Email di test inviata a ${testEmail}`),
    onError: (e: Error) => toast.error(`Errore: ${e.message}`),
  });

  const sourceColor = data?.source === 'db' ? 'var(--color-success)' : data?.source === 'env' ? 'var(--color-warning)' : 'var(--color-text-muted)';
  const sourceLabel = data?.source === 'db' ? 'Da DB' : data?.source === 'env' ? 'Da .env' : 'Nessuno';

  return (
    <Section title="Configurazione SMTP" collapsible defaultOpen={false}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ padding: '0.75rem', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-card)', borderLeft: '4px solid var(--color-warning)' }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text)', margin: 0 }}>⚠️ Modifiche applicate immediatamente senza riavvio server.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Origine:</span>
          <span style={{ fontSize: 'var(--text-xs)', color: sourceColor, fontWeight: 600 }}>{sourceLabel}</span>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
          <input type="checkbox" checked={form.smtpEnabled} onChange={e => setForm({ ...form, smtpEnabled: e.target.checked })} />
          Abilita SMTP
        </label>
        <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: '1rem' }}>
          <Input placeholder="smtp.gmail.com" value={form.smtpHost} onChange={e => setForm({ ...form, smtpHost: e.target.value })} />
          <Input type="number" min="1" max="65535" value={form.smtpPort} onChange={e => setForm({ ...form, smtpPort: parseInt(e.target.value) || 587 })} />
        </div>
        <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Input placeholder="user@gmail.com" value={form.smtpUser} onChange={e => setForm({ ...form, smtpUser: e.target.value })} />
          <Input type="password" placeholder={passSet ? '••••••••' : 'Password'} value={form.smtpPass} onChange={e => setForm({ ...form, smtpPass: e.target.value })} />
        </div>
        <Input placeholder="LensCRM <noreply@example.com>" value={form.smtpFrom} onChange={e => setForm({ ...form, smtpFrom: e.target.value })} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
          <input type="checkbox" checked={form.smtpSecure} onChange={e => setForm({ ...form, smtpSecure: e.target.checked })} />
          Usa SSL/TLS (porta 465) — altrimenti STARTTLS (porta 587)
        </label>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.75rem' }}>Test email</p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <Input placeholder="test@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" onClick={() => testMutation.mutate()} loading={testMutation.isPending} disabled={!form.smtpHost || !testEmail || saveMutation.isPending}>
              Invia test
            </Button>
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>Salva prima, poi testa l'invio</p>
        </div>
        <Button variant="primary" onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
          Salva configurazione SMTP
        </Button>
      </div>
    </Section>
  );
}
